import { PinSettingComponent } from '@dialogs/pin-setting/pin-setting.component';
import { PinsetSearchDialogComponent } from '@dialogs/pinset-search-dialog/pinset-search-dialog.component';
import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, DestroyRef, inject, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, ReactiveFormsModule, UntypedFormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatDrawer, MatSidenavModule } from '@angular/material/sidenav';
import { ActivatedRoute, Params, Router } from '@angular/router';
import { AnalyzeBreadcrumbData, ColorAliases, getFileNameWithoutExtension, ENTITY_TYPE_ICON_MAP } from '@mds/common';
import { MdsIcon, MdsIconsModule } from '@mds/icons';
import {
  BreadcrumbComponent,
  BreadcrumbData,
  ButtonComponent,
  FilterComponent,
  LoaderModule,
  SearchFilterComponent,
} from '@mds/ui';
import { AnalyzeFiltersService } from '@services/analyze.filters.service';
import { AnalyzeLayersService } from '@services/analyze.layers.service';
import { AnalyzeService } from '@services/analyze.service';
import { AttributeSearchDailogComponent } from '@dialogs/attribute-search-dialog/attribute-search-dialog.component';
import { EventDelegatorService } from '@services/event-delegator.service';
import { GlobalDataService } from '@services/global-data.service';
import { ToastrService } from '@services/toaster.service';
import { CookiesService } from '@services/cookies.service';
import { InlineManualService } from '@services/inline-manual.service';
import { MakersiteService } from '@services/makersite.service';
import _ from 'lodash';
import { ActiveAppLayer, MenuService } from '@services/menu.service';
import { CostingGridWrapperComponent } from './components/costing-grid-wrapper/costing-grid-wrapper.component';
import { DefaultGridWrapperComponent } from './components/default-grid-wrapper/default-grid-wrapper.component';
import { GraphWrapperComponent } from './components/graph-wrapper/graph-wrapper.component';
import { LayersHeaderComponent } from './components/layers-header/layers-header.component';
import { MapWrapperComponent } from './components/map-wrapper/map-wrapper.component';
import { OverviewComponent } from './components/overview/overview.component';
import { SankeyWrapperComponent } from './components/sankey-wrapper/sankey-wrapper.component';
import { SidePanelComponent } from './components/side-panel/side-panel.component';
import { OpenSidebarData } from './models/OpenSideBarData';
import {
  LayersAPIService,
} from './services/layers-api.service';
import { InviteShareComponent } from '@dialogs/share-invite/share-invite-dialog.component';
import { LocalStorageService } from '@services/local-storage.service';
import { getEntityTypeIcon } from '@utils/entity-type-icon';
import { InquiryDialogComponent } from '@dialogs/inquiry-dialog/inquiry-dialog.component';
import { finalize, map, Observable, Subject } from 'rxjs';
import { ReusableService } from '@services/reusable.service';
import { AiValidationDialogComponent } from '@dialogs/ai-validation-dialog/ai-validation-dialog.component';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';
import { LayersService } from '@pages/dashboards/scope-3/services/layers.service';
import { AIValidationStatuses } from '@pages/importers/components/importer-grid/importer-grid.model';

@Component({
  selector: 'app-layers',
  standalone: true,
  imports: [
    CommonModule,
    MatSidenavModule,
    SidePanelComponent,
    DefaultGridWrapperComponent,
    CostingGridWrapperComponent,
    GraphWrapperComponent,
    SankeyWrapperComponent,
    MapWrapperComponent,
    LayersHeaderComponent,
    FormsModule,
    ReactiveFormsModule,
    MdsIconsModule,
    OverviewComponent,
    LoaderModule,
    ButtonComponent
  ],
  templateUrl: './layers.component.html',
  styleUrls: ['./layers.component.scss'],
  // changeDetection: ChangeDetectionStrategy.OnPush
  providers: [AnalyzeLayersService, AnalyzeFiltersService, InlineManualService, LayersAPIService, LayersService]
})
export class LayersComponent implements OnInit, OnDestroy {
  @Input() data: any;
  @ViewChild('drawer') drawer: MatDrawer;
  icons = MdsIcon;
  isOpened = false;
  isLoading = true;
  destroyRef = inject(DestroyRef);
  backToDefault = false;
  svgResize = false;
  layer: string;
  rid: string;
  analyzeView: string;
  app: Record<any, string>;
  isSidePanelShow: boolean;
  analyzeDataset: any;
  ngxEventBus: any[] = [];
  path: AnalyzeBreadcrumbData[] = [];
  pins: any[];
  showSetDefaultView: boolean;
  public showAllData: UntypedFormControl = new UntypedFormControl(true);
  public groupLowImpactNodes: UntypedFormControl = new UntypedFormControl(true);
  public showTariffToggle: UntypedFormControl = new UntypedFormControl(true);
  isGroupNodesButtonEnabled = false;
  lastClickedNode: any;
  selectedNode: any;
  exactPath: any[] = [];
  breadcrumbData: BreadcrumbData<AnalyzeBreadcrumbData>[] = [];
  primaryColor = [ColorAliases.PRIMARY];
  showActivePinsFlag = false; // to show button of active pins in analyze component html page
  view = '';
  windowData: any = window;
  showAllToggleSwitch = false;
  sidepanelDetails: object;
  productInfo: object;
  sidepanelView = 'sidepanel';
  @ViewChild(CostingGridWrapperComponent)
  costingGridWrapperComponent!: CostingGridWrapperComponent;
  drawerMode = 'side';
  private menuService = inject(MenuService);
  @ViewChild('drawerContent') private drawerContent: HTMLElement;
  searchValue$ = new Subject<string>();
  allTariffOptions$: Observable<any>;
  tariffSchema: any;
  loadingAnalyzeData: boolean;

  private readonly layersAPIService = inject(LayersAPIService);
  private readonly layersService = inject(LayersService);
  private readonly analyzeService = inject(AnalyzeService);
  private readonly globalDataService = inject(GlobalDataService);
  private readonly makersiteService = inject(MakersiteService);
  private readonly cookiesService = inject(CookiesService);
  private readonly eventDelegatorService = inject(EventDelegatorService);
  private readonly dialog = inject(MatDialog);
  private readonly toastrService = inject(ToastrService);
  private readonly analyzeLayersService = inject(AnalyzeLayersService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly localStorageService = inject(LocalStorageService);
  private readonly cd = inject(ChangeDetectorRef);
  private readonly reusableService = inject(ReusableService);
  updatingAIStatus = false;

  ngOnInit(): void {
    this.data = _.cloneDeep(this.route.snapshot.params);
    this.globalDataService.setCurrentActiveApp({ name: 'app.analyze' });

    this.globalDataService.nodeEvent$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((item) => {
      if (!item) return;
      const itemData = item.data;
      // Now using the product rid to fetch the details of select product. Commented the old code below for future reference.
      let rid = itemData.rid;
      // let { rid } = itemData;
      if (rid) {
        rid = rid.replace('#', '');
        this.populateSidePanelForRid(rid);
      }
    });

    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.view = params.view;
    });

    this.route.paramMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params: Params) => {
      this.data = {
        layer: params.get('layer') || '',
        rid: params.get('rid') || '',
        view: this.view
      };
      this.analyzeDataset = undefined;
      this.init();
    });

    this.ngxEventBus.push(
      this.eventDelegatorService.on('ngx-event-analyze', (ev) => {
        this.parseAllMenuEvent(ev);
      })
    );

    this.layersService.tariffSchema$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((res) => {
      this.tariffSchema = res;
      if (this.tariffSchema) {
        this.searchValue$.next('');
      }
    });

    // TODO fix this
    this.menuService.selectedAction$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((action) => {
      this.parseAllMenuEventNew(action);
    });

    this.isLoading = true;
    this.analyzeService
      .getAnalyzeData()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.analyzeDataset = res;
        this.isLoading = false;
        if (!this.analyzeDataset) return;
        this.isRestrictedView();
        this.openSidePanel();
        this.setToggleSwitch();
      });

    this.analyzeService
      .getBreadcrumbPath()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res: AnalyzeBreadcrumbData[]) => {
        this.path = res;
        this.setBreadcrumbData();
      });

    this.analyzeService
      .getPinsAsObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        this.switchSidepanel(res?.length < 1 ? 'sidepanel' : 'performance-analytics');
        this.pins = res;
      });

    this.globalDataService
      .getHasGroupedNodes()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => {
        if (res) {
          this.isGroupNodesButtonEnabled = false;
          this.groupLowImpactNodes.enable();
        } else {
          this.groupLowImpactNodes.disable();
          this.isGroupNodesButtonEnabled = true;
        }
      });

    this.menuService.reload$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((reload) => {
      if (reload) {
        this.reloadContent();
      }
    });

    this.allTariffOptions$ = this.searchValue$.pipe(
      distinctUntilChanged(),
      debounceTime(500),
      switchMap((search) => {
        const optUrl = search ? this.tariffSchema.options : this.tariffSchema.default_options;
        const url = this.generatingUrl(optUrl, search);
        return this.makersiteService.getBaseData(url).pipe(
          takeUntilDestroyed(this.destroyRef),
          map((apiResults: any[]) =>
            apiResults.map((line) => ({
              ...line,
              text: line.name
            }))
          )
        );
      })
    );
  }

  ngOnDestroy(): void {
    this.ngxEventBus.forEach((e) => {
      e?.unsubscribe();
    });
  }

  onOpenSidebar(event: OpenSidebarData) {
    this.parseAllMenuEvent(event);
  }

  setBreadcrumbData() {
    this.breadcrumbData = this.path.map((path, i) => ({
      key: path.id?.toString() ?? path.rid,
      label: path.name,
      linkUrl: '',
      openInNewTab: false,
      value: path,
      index: i
    }));
  }

  onGridEvent(event: any) {
    this.parseAllGridEvent(event);
  }

  /**
   * Handles all menu events in analyze.
   */
  parseAllMenuEvent(event: any): void {
    const key = event.action?.key;
    const validViews = new Set(['grid', 'sankey', 'graph', 'pinmap', 'routemap', 'heatmap', 'choropleth']);

    if (validViews.has(key)) {
      this.setView(key);
      return;
    }
    switch (event.type) {
      case 'mega-action-event':
        this.megaMenuAnalyzeEvents(event);
        break;
      case 'mega-menu-event':
        this.handleMegaMenuEvent(event);
        break;
      case 'ms-search-event':
        this.msDataSelection(event);
        break;
      case 'attribute-pin-apply':
        this.applyAttributePin(event.data);
        break;
      case 'toolbar-reload-event':
        this.reloadContent();
        break;
      case 'pinmap-events':
        this.populateSidePanel(event.data);
        this.openSidePanel(event.data);
        break;
      case 'showInfoNode':
        this.openSidePanel(event.data);
        break;
      default:
        this.rightClickMenuEvent(event);
    }
  }

  parseAllMenuEventNew(event: any): void {
    const key = event?.key;
    const validViews = new Set(['grid', 'sankey', 'graph', 'pinmap', 'routemap', 'heatmap', 'choropleth']);

    if (validViews.has(key)) {
      this.setView(key);
    }

    this.megaMenuAnalyzeEventsNew(key);
  }

  openInviteDialog() {
    this.dialog.open(InviteShareComponent, {
      data: { type: 'showInvite', value: 'Invite' },
      panelClass: 'show-invite'
    });
  }

  openShareDialog() {
    const userInfo = this.localStorageService.getItem('user_info');
    const link = `/mattermaps/${this.layer}/${this.rid}`;
    const shareData = {
      type: 'showInvite',
      value: 'Share',
      data: {
        fr: userInfo.email,
        link,
        rid: this.rid,
        usersLackingAccessList: ''
      }
    };
    this.dialog.open(InviteShareComponent, {
      data: shareData,
      panelClass: 'show-invite'
    });
  }

  /**
   * Handles side panel events.
   */
  sidepanelEvent(event: any): void {
    const { type } = event.value;

    const actionsMap: Record<string, () => void> = {
      ecofee: () => {}, // Placeholder for ecofeeQustions if needed
      reload: () => this.reloadContent(),
      close: () => this.closeSidepanel(),
      open: () => this.openDrawer(),
      pins: () => this.applyPinFromSidepanel()
    };

    if (actionsMap[type]) {
      actionsMap[type]();
    }
  }

  /**
   * Handles switching views based on the mega menu selection.
   */
  setView(key: string): void {
    if (this.layer === 'costing' && key === 'grid') {
      key = 'costing-grid';
    }

    if (this.layer === 'overview') {
      this.globalDataService.setMattermapView(undefined);
      return;
    }

    if (key === 'choropleth' || key === 'heatmap') {
      this.drawerMode = 'over';
    } else {
      this.drawerMode = 'side';
    }

    if (this.analyzeView === key) return;

    this.analyzeService.setAppSwitchStatus(false);
    this.layersService.setTariffSchema(null);
    this.toggleSidePanel(key);

    this.globalDataService.setMattermapView(key === 'costing-grid' ? 'grid' : key);
    const updateKey = this.analyzeView === 'costing-grid' ? 'grid' : this.analyzeView;
    this.menuService.updateMenuItem(updateKey, { selected: false });
    this.analyzeView = key;
    this.menuService.updateMenuItem(this.analyzeView === 'costing-grid' ? 'grid' : key, { selected: true });
    this.analyzeService.analyzeView = key;

    this.isRestrictedView();
    this.setToggleSwitch();

    if (this.analyzeService.getGridDrillingMode()) {
      this.initAnalyzeData();
    }
  }

  /**
   * Toggles the side panel visibility based on the selected view.
   */
  toggleSidePanel(view: string): void {
    setTimeout(() => this.drawer?.toggle(), 0);
  }

  windowOpen(url: string): void {
    this.router.navigate([url]);
  }

  /** ************************ Breadcrumb Navigation **************************/

  openBreadcrumbNode(index: number): void {
    this.path.splice(index + 1);
    this.analyzeService.updateBreadcrumbPath(this.path);
    this.selectedNode = _.cloneDeep(this.path[this.path.length - 1]);
    this.initAnalyzeData();
  }

  onBreadcrumbSelect(breadcrumbData: BreadcrumbData<AnalyzeBreadcrumbData>): void {
    this.openBreadcrumbNode(breadcrumbData.index);
  }

  /** ************************ View Settings & Events **************************/

  backToDefaultView(): void {
    this.showSetDefaultView = false;
    this.backToDefault = true;
    this.emitCustomEvent('back-to-default');
  }

  onGroupSmallNodesToggle(event: boolean): void {
    this.groupLowImpactNodes.setValue(event);
    this.groupSmallNodes();
  }

  onShowAllToggle(event: boolean): void {
    this.showAllData.setValue(event);
    this.toggleShowAllDataSwitch();
  }

  onTariffToggle(event: boolean): void {
    this.showTariffToggle.setValue(event);
    this.globalDataService.setTariffApplied(event);
    this.costingGridWrapperComponent.applyTariff();
  }

  onSelectTariff(event) {
    this.globalDataService.setAnalysisTariff({ [this.tariffSchema.key]: event[0].value });
    if (this.globalDataService.getTariffApplied()) {
      this.costingGridWrapperComponent.applyTariff();
    }
  }

  generatingUrl(url, value) {
    return url.replace('<keyword>', encodeURIComponent(value));
  }

  onSearchTariff(search) {
    this.searchValue$.next(search);
  }

  getSidePanelDetails(rid: string): void {
    this.layersAPIService.getSidepanelDetails(rid, this.app.layer, this.app.module).subscribe((res) => {
      this.sidepanelDetails = res;
    });
    this.cd.detectChanges();
  }

  switchSidepanel(view) {
    this.sidepanelView = view;
  }

  private sanitizeRid(rid: string): string {
    return rid.replace('#', '');
  }

  private init(): void {
    const { layer, rid } = this.data;
    const sanitizedRid = this.sanitizeRid(rid);

    if (this.analyzeService.getAnalyzeRid() !== sanitizedRid) {
      this.resetLayerState();
    }

    this.layer = layer;
    this.rid = sanitizedRid;
    this.analyzeService.setLayer(this.layer);
    this.analyzeService.setAnalyzeRid(this.rid);

    this.prepareAnalysis();

    if (this.layer === 'product') {
      this.windowOpen(`/mattermaps/overview/${this.rid}`);
    }

    this.initMegaMenu();
    this.handleSankeyGroupCookie();
  }

  private resetLayerState(): void {
    this.analyzeService.setPins([]);
    this.analyzeService.updateBreadcrumbPath([]);
  }

  private prepareAnalysis(): void {
    this.analyzeService.setAnalyzeDataNew(undefined);
    this.globalDataService.setAnalysisFilter(false);
    this.isLoading = true;
  }

  private handleSankeyGroupCookie(): void {
    let isGrouped = this.cookiesService.getCookie('sankey_group');

    // Default to true if no cookie exists
    if (isGrouped === null) {
      isGrouped = 'true';
    }

    const isGroupedBoolean = isGrouped === 'true';
    this.cookiesService.setCookie('sankey_group', isGroupedBoolean.toString());

    this.isGroupNodesButtonEnabled = isGroupedBoolean;
    this.groupLowImpactNodes.setValue(isGroupedBoolean);
  }

  private initMegaMenu(): void {
    // TODO need update on this
    this.globalDataService.setUrlParam(this.data);
    this.globalDataService.setAllActions([]);
    if (this.layer) {
      this.getLayers();
    }
  }

  private getLayers(): void {
    this.globalDataService.setActiveApp(undefined);
    this.makersiteService.getMattermapLayers().subscribe((res: any) => {
      const { layers } = res;
      this.globalDataService.setMegaMenuList(layers);

      const initView = this.findAndSetActiveLayer(layers);

      this.updateGlobalServices();

      const view = this.determineView(initView);
      this.initMattermapActions(); // TODO remove this
      this.initMenuActions(view);
      this.getMattermapData();
    });
  }

  private findAndSetActiveLayer(layers: any[]) {
    const defaultToolList = ['sankey', 'choropleth', 'heatmap', 'grid'];
    let initView = this.data.view || 'sankey';
    for (const category of layers) {
      const layer = category.list.find((l: any) => l.layer === this.layer);
      if (layer) {
        layer.tool_list = defaultToolList;

        if (['pinmap', 'graph'].includes(layer.tool)) {
          initView = this.data.view || 'graph';
          layer.tool_list = ['graph', 'pinmap', 'grid'];
          // TODO view missing check
        }

        this.app = layer;
        break;
      }
    }
    this.getSidePanelDetails(this.rid);
    return initView;
  }

  private updateGlobalServices(): void {
    if (!this.app) return;

    this.globalDataService.setActiveApp(this.app);
    this.globalDataService.setPPMLayer(this.app);

    // Reset attribute filters if the layer is not 'company'
    if (this.app.layer !== 'company') {
      this.analyzeService.setAttributeFilters([]);
    }
  }

  private determineView(view: string): string {
    if (this.analyzeService.getAppSwitchStatus() && this.globalDataService.getMattermapView()) {
      view = this.globalDataService.getMattermapView();
      const viewMapping: { [key: string]: string } = {
        'costing-grid': 'grid',
        choropleth: 'pinmap',
        heatmap: 'pinmap',
        pinmap: 'choropleth',
        sankey: 'graph',
        graph: 'sankey',
        routemap: 'sankey'
      };

      view = viewMapping[view] || view;

      if (!this.app.tool_list.includes(view)) {
        view = this.app.tool_list[0];
      }
    }
    return view;
  }

  private initMattermapActions() {
    const allActionsValidation = {
      show: ['share', 'invite', 'pins'],
      hidden: [],
      disabled: []
    };

    allActionsValidation.show = allActionsValidation.show.concat(this.app.tool_list);
    if (this.layer === 'overview') {
      allActionsValidation.disabled = ['sankey', 'choropleth', 'heatmap', 'grid'];
      allActionsValidation.show = [...this.app.tool_list, 'share', 'invite'];
    } else if (this.layer === 'dpm') {
      allActionsValidation.show = ['share', 'invite', 'pins'];
    } else if (this.layer === 'routemap') {
      allActionsValidation.show.push('routemap');
    } else if (this.layer === 'company') {
      allActionsValidation.show.push('filters');
    } else if (['substance_risk', 'chemical_grading'].includes(this.layer)) {
      allActionsValidation.show.push('substances');
    } else if (['recycled_content', 'eol_rir'].includes(this.layer)) {
      allActionsValidation.show = allActionsValidation.show.filter((e) => e !== 'pins');
    }

    const allActions = {
      tabs: [
        { key: 'sankey', value: 'Sankey' },
        { key: 'graph', value: 'Graph' },
        { key: 'choropleth', value: 'Choropleth' },
        { key: 'heatmap', value: 'Heatmap' },
        { key: 'routemap', value: 'Routemap' },
        { key: 'pinmap', value: 'Pinmap' },
        { key: 'grid', value: 'Grid' }
      ],
      actions: [
        { key: 'save', value: 'Save' },
        { key: 'pins', value: 'Pins' },
        { key: 'substances', value: 'Substances' },
        { key: 'export_png', value: 'Export Png' },
        {
          key: 'filters',
          value: 'Filters',
          dropdown: true,
          list: [
            { key: 'filters', value: 'Apply filter to selected products' },
            { key: 'active_filters', value: 'Active filters list' }
          ]
        }
      ],
      commonActivity: [
        { key: 'share', value: 'Share' },
        { key: 'invite', value: 'Invite' }
      ],
      validations: allActionsValidation
    };

    this.globalDataService.setAllActions(allActions);
  }

  private initMenuActions(view?: string) {
    let centerActions = [
      {
        label: 'Sankey',
        key: 'sankey',
        icon: MdsIcon.SANKEY,
        selected: true,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Graph',
        key: 'graph',
        icon: MdsIcon.GRAPH,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Choropleth',
        key: 'choropleth',
        icon: MdsIcon.CHOREPETH,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Heatmap',
        key: 'heatmap',
        icon: MdsIcon.HEATMAP,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Routemap',
        key: 'routemap',
        icon: MdsIcon.HEATMAP,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Pinmap',
        key: 'pinmap',
        icon: MdsIcon.PINMAP,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Grid',
        key: 'grid',
        icon: MdsIcon.GRID,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      }
    ];
    const rightActions = [
      {
        label: 'Edit',
        key: 'edit',
        icon: MdsIcon.EDIT,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Save',
        key: 'save',
        icon: MdsIcon.SAVE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      },
      {
        label: 'Substances',
        key: 'substances',
        icon: MdsIcon.DOWNLOAD,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      },
      {
        label: 'Export Png',
        key: 'export_png',
        icon: MdsIcon.DOWNLOAD,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false
      },
      {
        label: 'Filters',
        key: 'filters',
        icon: MdsIcon.FILTER,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: false,
        dropdown: true,
        list: [
          { key: 'filters', value: 'Apply filter to selected products' },
          { key: 'active_filters', value: 'Active filters list' }
        ]
      },
      {
        label: 'Share',
        key: 'share',
        icon: MdsIcon.SHARE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Invite',
        key: 'invite',
        icon: MdsIcon.INVITE,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      },
      {
        label: 'Pins',
        key: 'pins',
        icon: MdsIcon.PIN,
        selected: false,
        disabled: false,
        menuTrigger: '',
        visible: true
      }
    ];
    centerActions = centerActions.map((action) => {
      action.visible = this.app.tool_list.includes(action.key);
      return action;
    });
    if (['overview', 'dpm'].includes(this.layer)) {
      // new
      centerActions = centerActions.map((action) => {
        if (['sankey', 'choropleth', 'heatmap', 'grid'].includes(action.key)) {
          action.visible = false;
          action.disabled = true;
          action.selected = false;
        }
        return action;
      });
    } else if (this.layer === 'routemap') {
      centerActions.find((action) => action.key === 'routemap').visible = true;
      centerActions.find((action) => action.key === this.data.view).selected = true;
    } else if (this.layer === 'company') {
      rightActions.find((action) => action.key === 'filters').visible = true;
    } else if (['substance_risk', 'chemical_grading'].includes(this.layer)) {
      rightActions.find((action) => action.key === 'substances').visible = true;
    } else if (['recycled_content', 'eol_rir'].includes(this.layer)) {
      rightActions.find((action) => action.key === 'pins').visible = false;
    }
    let appIconName = getFileNameWithoutExtension(this.app.icon_app);
    appIconName = `APP_${appIconName.replaceAll('-', '_').toUpperCase()}`;

    this.makersiteService.getTileCards(this.data.rid).subscribe((results) => {
      this.productInfo = results;
      const entity = results[0];
      const entityIcons = [{ key: getEntityTypeIcon(entity.stage) }];
      if (entity.is_forked && entity.stage !== 'makersite') {
        entityIcons.unshift({ key: MdsIcon.FORKED });
      }
      const activeLayer: Partial<ActiveAppLayer> = this.app;
      this.menuService.setMenuConfig({
        activeAppOld: activeLayer,
        activeApp: {
          mainIcon: {
            icon: MdsIcon[appIconName] ?? MdsIcon.DEFAULT_IMPORT
          },
          rid: this.app?.rid,
          name: this.app?.app_name,
          icons: [],
          subtitle: '',
          menuTrigger: 'selectApps',
          category: this.app.category,
          layer: this.app.layer
        },
        activeEntity: {
          mainIcon: {
            icon: ENTITY_TYPE_ICON_MAP[entity.cardType],
            pathColors: ['#fff']
          },
          type: entity.cardType,
          rid: entity?.rid,
          name: entity?.title,
          icons: entityIcons,
          subtitle: entity.card_type_label
        },
        centerActions,
        rightActions
      });
      // Show selected Tab based on screen e.g. "Sankey", "Graph"...
      this.setView(view);
    });
  }

  private getMattermapData() {
    // this is actual initialization of mattermap data
    if (['overview', 'dpm'].includes(this.app.layer)) {
      return;
    }
    this.resetToggleSwitch();
    this.initAnalyzeData();
  }

  private resetToggleSwitch(): void {
    this.showAllData.setValue(false);
    this.showTariffToggle.setValue(false);
    this.globalDataService.setAnalysisFilter(false);
  }

  private initAnalyzeData(isDrill = false, isFromGrid = false): void {
    const activePins = this.getActivePins();
    if (activePins.length > 0 || this.path.length > 1) {
      this.getAnalyzeDataWithFilters(isDrill, isFromGrid);
      return;
    }

    this.getAnalyzeData(isDrill);
  }

  private getActivePins(): any[] {
    const selectedPins = this.pins.filter((pin) => pin.checkbox && !pin.hidden);

    return this.layer === 'company' ? [...selectedPins, ...this.analyzeService.getAttributeFilters()] : selectedPins;
  }

  private getAnalyzeData(isDrill: boolean): void {
    const { rid, amount, tier } = this.getLastNodeDetails();
    if (!rid || !this.layer) return;

    this.analyzeService.setGridDrillingMode(false); // Detect grid API calls

    if (this.isGraphTool()) {
      this.fetchGraphAnalyzeData(rid, amount, tier, isDrill);
    } else {
      this.fetchPPMAnalyzeData(rid, amount, tier, isDrill);
    }
  }

  private isGraphTool(): boolean {
    return ['pinmap', 'graph'].includes(this.app?.tool);
  }

  private fetchGraphAnalyzeData(rid: string, amount: any, tier: any, isDrill: boolean): void {
    this.loadingAnalyzeData = true;

    this.makersiteService
      .getGraph(rid, this.layer, amount, tier)
      .pipe(
        finalize(() => {
          setTimeout(() => {
            this.loadingAnalyzeData = false;
          }, 500);
        })
      )
      .subscribe(
        (res: any) => this.analyzeService.setAnalyzeDataNew(res, isDrill),
        (error: any) => this.errorHandler(error, rid)
      );
  }

  private fetchPPMAnalyzeData(rid: string, amount: any, tier: any, isDrill: boolean): void {
    this.loadingAnalyzeData = true;

    this.makersiteService
      .getPPM(rid, this.layer, amount, tier)
      .pipe(
        finalize(() => {
          setTimeout(() => {
            this.loadingAnalyzeData = false;
          }, 500);
        })
      )
      .subscribe(
        (res: any) => this.analyzeService.setAnalyzeDataNew(res, isDrill),
        (error: any) => this.errorHandler(error, rid)
      );
  }

  private getLastNodeDetails(): { rid: string; amount?: any; tier?: any } {
    const lastNode: any = this.path.length > 0 ? this.path[this.path.length - 1] : null;
    return {
      rid: lastNode?.rid || this.rid,
      amount: lastNode?.amount,
      tier: lastNode?.tier
    };
  }

  private fetchAnalyzeData(
    apiMethod: (rid: string, layer: string, amount?: any, tier?: any) => any,
    rid: string,
    amount: any,
    tier: any,
    isDrill: boolean
  ): void {
    apiMethod.call(this.makersiteService, rid, this.layer, amount, tier).subscribe(
      (res: any) => this.analyzeService.setAnalyzeDataNew(res, isDrill),
      (error: any) => this.errorHandler(error, rid)
    );
  }

  /**
   *
   * @param isDrill - this is true when drill-down
   * @param isFromGrid - this is true when call is getting from grid
   */
  private getAnalyzeDataWithFilters(isDrill: boolean, isFromGrid: boolean): void {
    const activePins = this.getActivePins();
    const lastNode = this.getLastNode();
    const gridMode = this.analyzeView === 'grid';
    const rid = lastNode?.rid || this.rid;
    if (!rid || !this.layer) return;
    this.analyzeService.setGridDrillingMode(gridMode); // Detect grid API calls

    const payload: any = { pins: activePins };
    if (this.path.length > 1) {
      payload.node = this.cleanNode(lastNode);
    }
    // Ensure proper pin handling for specific layers
    if (['recycled_content', 'eol_rir'].includes(this.layer)) {
      payload.pins = [];
    }

    // Fetch data using the appropriate API call
    this.loadingAnalyzeData = true;
    if (['pinmap', 'graph'].includes(this.app?.tool)) {
      // this.fetchGraphAnalyzeData(rid, payload, gridMode, isDrill);
      this.makersiteService
        .applyPinsGraph(rid, this.layer, payload, gridMode)
        .pipe(
          finalize(() => {
            setTimeout(() => {
              this.loadingAnalyzeData = false;
            }, 500);
          })
        )
        .subscribe(
          (res: any) => {
            this.analyzeService.setAnalyzeDataNew(res, isDrill, isFromGrid);
          },
          (error) => {
            this.errorHandler(error, rid);
          }
        );
    } else {
      // this.fetchPPMAnalyzeData(rid, payload, gridMode, isDrill);
      this.makersiteService
        .applyPinsPPM(rid, this.layer, payload, gridMode)
        .pipe(
          finalize(() => {
            setTimeout(() => {
              this.loadingAnalyzeData = false;
            }, 500);
          })
        )
        .subscribe(
          (res: any) => {
            if (!res.message) {
              if (gridMode && !isDrill) {
                res.nodes = this.addBreadcrumbNodeRoot(payload, res);
              }
              this.analyzeService.setAnalyzeDataNew(res, isDrill, isFromGrid);
            } else {
              this.toastrService.showMessage(res.message, 3000);
            }
          },
          (error) => {
            this.errorHandler(error, rid);
          }
        );
    }
  }

  private getLastNode(): any {
    return this.selectedNode || this.path[this.path.length - 1] || {};
  }

  private cleanNode(node: any): any {
    const cleanNode = structuredClone(node); // Efficient deep copy
    delete cleanNode.sourceLinks;
    delete cleanNode.targetLinks;
    return cleanNode;
  }

  private errorHandler(error: any, rid: string): void {
    if (!error) return;

    this.isLoading = false;

    // Handle unauthorized dataset errors
    this.analyzeService.unauthorizedDatasetNew(error, rid, true);
  }

  private addBreadcrumbNodeRoot(payload: any, res: any): any[] {
    if (!res?.nodes?.length || res.nodes[0].id === 0) {
      return res.nodes || []; // Return original nodes if empty or already has root
    }

    // Create a new root node
    const rootNode = {
      ...payload.node,
      id: 0,
      parents: [0]
    };

    // Update parents for existing nodes
    const updatedNodes = res.nodes.map((node) => ({
      ...node,
      parents: [0, ...(node.parents || [])] // Ensures 'parents' is an array
    }));

    return [rootNode, ...updatedNodes];
  }

  /** ************************ Analyze Page Event Handlers **************************/

  private parseAllGridEvent(event: any): void {
    const { action, type, data, rows, nodes, status } = event;

    // block all events until we get the response
    if (this.loadingAnalyzeData) return;

    switch (type) {
      case 'action':
        this.handleGridAction(action, data);
        break;

      case 'selection':
        this.handleGridSelection(rows, nodes);
        break;

      case 'rowDoubleClick':
        this.handleGridRowDoubleClick(event);
        break;

      case 'restoreExpand':
        this.restoreExpandedGridNode(event);
        break;

      case 'updateAIValidationStatus':
        this.changeSelectedNodeStatusFromGrid(status);
        break;

      default:
        break;
    }
  }

  /**
   * Handles action events in the grid.
   */
  private handleGridAction(action: string, data: any): void {
    if (action === 'view') {
      window.open(`mattermaps/overview/${this.sanitizeRid(data.rid)}`, '_blank');
    } else if (['pin', 'alternatives', 'pin_applied'].includes(action)) {
      this.showPinSearchModal({ action, data });
    }
  }

  /**
   * Handles selection events in the grid.
   */
  private handleGridSelection(rows: any[], nodes: any[]): void {
    if (!rows.length || this.lastClickedNode === rows[0]) return;

    this.exactPath = [];
    this.lastClickedNode = rows[0];
    const targetNode = rows[0].type === 'product' ? nodes[0] : nodes[0]?.parent;

    this.findExactPath(targetNode);

    if (rows[0].rid) {
      this.populateSidePanelForRid(rows[0].rid);
    }
    this.openSidePanel(rows[0]);
  }

  /**
   * Handles row double-click events in the grid.
   */
  private handleGridRowDoubleClick(event: any): void {
    const isLeaf = event.event.node.allLeafChildren.length === 0;
    if (isLeaf && this.analyzeView !== 'costing-grid') {
      event.event.node.setExpanded(true);
      this.drillToNode(event, true);
    } else if (this.analyzeView === 'grid') {
      this.analyzeService.setGridDrillingMode(true);
      this.selectedNode = event.data;
    }
  }

  private restoreExpandedGridNode(event: any): void {
    const node = event.data;

    if (!node || !this.hasValidDataForDrilling(node, true)) {
      return;
    }

    this.drillToNode(event, true, true);
  }

  /**
   * Finds the exact path to the root node in the Grid view.
   */
  private findExactPath(node: any): void {
    if (!node?.data) return;

    const isCostingGrid = this.analyzeView === 'costing-grid' && node.data.data_type === 'process_parent';
    if (isCostingGrid || node.data.type === 'product') {
      this.exactPath.push(node.data);
    }

    if (node.parent?.id === 'ROOT_NODE_ID') {
      this.analyzeService.updateBreadcrumbPath(this.exactPath.reverse());
      return;
    }

    this.findExactPath(node.parent);
  }

  /**
   * Handles mega menu switching.
   */
  private handleMegaMenuEvent(event: any): void {
    if (event.action === 'switch-menu') {
      this.analyzeView = null;
      this.analyzeService.setAppSwitchStatus(true);
      this.analyzeLayersService.switchApp(event);
    }
  }

  /**
   * Handles right-click events on sankey and graph.
   */
  private rightClickMenuEvent(event: any): void {
    const { action, data } = event;
    const actionsMap: Record<string, () => void> = {
      substitute: () => this.showPinSearchModal(event),
      alternatives: () => this.showPinSearchModal(event),
      edit: () => window.open(`mattermaps/overview/${this.sanitizeRid(data.rid)}/edit`, '_blank'),
      editOriginal: () => window.open(`mattermaps/overview/${this.sanitizeRid(data.rid_original)}/edit`, '_blank'),
      navigate: () => this.drillToNode(event),
      todo: () => this.todoTaskDialog(),
      zoom: () => {
        this.showSetDefaultView = true;
      },
      onclickLink: () => this.openSidePanelFromSankeyLink()
    };
    if (actionsMap[action]) {
      actionsMap[action]();
    }
  }

  /**
   * Handles mega menu actions like share, invite, and filters.
   */
  private megaMenuAnalyzeEvents(event: any): void {
    const key = event.action?.key;

    const actionsMap: Record<string, () => void> = {
      substances: () => this.analyzeService.exportSubstances(),
      export_png: () => this.analyzeService.exportPng(),
      pins: () => this.openPinSettingModal(),
      share: () => this.analyzeService.share(),
      invite: () => this.analyzeService.invite(),
      save: () => this.analyzeService.saveState(this.analyzeView),
      filters: () => this.filterSearchModal(),
      active_filters: () => this.filterSettingModal()
    };

    if (actionsMap[key]) {
      actionsMap[key]();
    }
  }

  private megaMenuAnalyzeEventsNew(key: string): void {
    const actionsMap: Record<string, () => void> = {
      substances: () => this.analyzeService.exportSubstances(),
      export_png: () => this.analyzeService.exportPng(),
      pins: () => this.openPinSettingModal(),
      share: () => this.openShareDialog(),
      invite: () => this.openInviteDialog(),
      save: () => this.analyzeService.saveState(this.analyzeView),
      filters: () => this.filterSearchModal(),
      active_filters: () => this.filterSettingModal(),
      edit: () => {
        this.router.navigate(['edit'], {
          relativeTo: this.route
        });

        this.menuService.clearSelectedAction();
      }
    };

    if (actionsMap[key]) {
      actionsMap[key]();
    }
  }

  /**
   * Reloads content unless the layer is 'overview' or 'dpm'.
   */
  private reloadContent(): void {
    if (!['overview', 'dpm'].includes(this.layer)) {
      this.initAnalyzeData();
      if (this.productInfo) {
        this.populateSidePanelForRid(this.sanitizeRid(this.productInfo[0].rid));
      }
    }
  }

  /** ************************ Events End **************************/

  private msDataSelection(event: any): void {
    const rid = this.sanitizeRid(event.item.rid);
    const url = `mattermaps/${this.layer || 'overview'}/${rid}`;
    this.router.navigate([url]);
  }

  /** ************************ Pin Management **************************/

  private openPinSettingModal(latestPin?: any): void {
    const allPins = latestPin ? [..._.cloneDeep(this.pins), latestPin] : _.cloneDeep(this.pins);
    const data = {
      pins: allPins,
      gridSchema: this.analyzeService.getPinGridSchema()
    };

    const dialogRef = this.dialog.open(PinSettingComponent, {
      data
    });

    dialogRef.afterClosed().subscribe((pins) => {
      if (!pins) return;
      this.analyzeService.setPins(pins);
      this.initAnalyzeData();
    });
  }

  /**
   * Apply pins from side panel.
   */
  private applyPinFromSidepanel(): void {
    const data = {
      action: 'substitute',
      controller: window.location.pathname,
      data: this.path[this.path.length - 1],
      type: 'sankey'
    };
    this.showPinSearchModal(data);
  }

  private showPinSearchModal(event: { data: any; action: string }): void {
    const { data: node, action } = event;

    const dialogRef = this.dialog.open(PinsetSearchDialogComponent, {
      panelClass: 'pinsearch-model-container',
      data: {
        gridSchema: this.data.pin_grid_schema,
        node,
        action,
        isFrom: 'ngx'
      }
    });

    dialogRef.afterClosed().subscribe((res) => {
      if (!res) return;
      this.selectedNode = _.cloneDeep(this.exactPath[0]);
      this.exactPath = [];
      this.setRouteAndPins(node, res);
    });
  }

  private setRouteAndPins(node: any, pinData: any): void {
    const { pin } = pinData.data;
    pin.info.route = this.buildPinRoute(node, pin);
    pin.checkbox = true;
    pin.replacementRid = pinData.data.rid;
    this.openPinSettingModal(pin);
  }

  /**
   * Builds the route path for a selected pin.
   */
  private buildPinRoute(node: any, pin: any): string[] {
    if (!node) return [];

    const route: string[] = [];
    if (['grid', 'costing-grid'].includes(this.analyzeView)) {
      this.path.forEach((e) => route.push(e.rid));
      route.push(pin.info.domain.rid);
    } else {
      const routeNodeList = this.analyzeDataset.nodes.filter((e) => node.parents.includes(e.id));
      routeNodeList.sort((a, b) => a.parents.length - b.parents.length);

      routeNodeList.forEach((value, key) => {
        if (!routeNodeList[key + 1]) return;
        const str = value.processrid
          ? `${value.processrid}~${routeNodeList[key + 1].rid}|${routeNodeList[key + 1].rid_original}`
          : `${value.rid}~${routeNodeList[key + 1].rid}`;
        route.push(str);
      });
    }

    return route;
  }

  /** ************************ View Restrictions **************************/

  private isRestrictedView(): void {
    if (!this.analyzeDataset?.nodes?.length) return;

    const nodeCount = this.analyzeDataset.nodes.length;
    const isSankey = this.analyzeView === 'sankey';
    const isGraph = this.analyzeView === 'graph';
    const isCostingLayer = this.app.layer === 'costing';

    const SANKEY_LIMITS = { costing: 1000, other: 500 };
    const GRAPH_LIMIT = 250;

    if (
      (isSankey &&
        ((isCostingLayer && nodeCount > SANKEY_LIMITS.costing) ||
          (!isCostingLayer && nodeCount > SANKEY_LIMITS.other))) ||
      (isGraph && nodeCount > GRAPH_LIMIT)
    ) {
      this.toastrService.showMessage('This model is too large for Visualization. Please use the grid', 6000);
      this.setView('grid');
    }
  }

  /** ************************ Node Drilling **************************/

  private isNodeDrillAble(node: any, lastNode: any): boolean {
    const lastNodeRid = this.sanitizeRid(lastNode?.rid);
    const currentNodeRid = this.sanitizeRid(node?.rid);

    if (this.analyzeView === 'sankey') {
      return currentNodeRid === this.rid || (lastNodeRid === currentNodeRid && lastNode.Tier === node.Tier);
    }

    return this.analyzeView === 'grid'
      ? currentNodeRid === this.rid
      : lastNodeRid === currentNodeRid || currentNodeRid === this.rid;
  }

  private drillToNode(event: any, isFromGrid = false, suppressToast = false): void {
    const node = event.data;
    const lastNode = _.cloneDeep(this.path[this.path.length - 1]);

    if (this.isNodeDrillAble(node, lastNode)) {
      if (!suppressToast) {
        this.toastrService.showMessage(`You are already viewing ${node.name || node.title}.`, 3000);
      }
      return;
    }

    if (!this.hasValidDataForDrilling(node, suppressToast)) return;

    this.selectedNode = node;
    this.updateBreadcrumb(node);
    this.initAnalyzeData(true, isFromGrid);
  }

  /**
   * Checks if the selected node has valid data for drilling.
   */
  private hasValidDataForDrilling(node: any, suppressToast = false): boolean {
    const activePins = this.analyzeService.getPins();
    if (activePins.length === 0 && node.in_process === null) {
      if (!suppressToast) {
        this.toastrService.showMessage('No data inside node.', 3000);
      }
      return false;
    }
    return true;
  }

  /**
   * Updates the breadcrumb when drilling into a node.
   */
  private updateBreadcrumb(node: any): void {
    if (['process', 'product'].includes(node.type)) {
      if (this.analyzeView === 'grid' && this.path[this.path.length - 1].rid === node.rid) {
        this.path.pop();
        this.path.push(node);
        this.analyzeService.updateBreadcrumbPath(this.path);
      } else {
        this.analyzeService.updateBreadcrumbPath(node);
      }
    }
  }

  /** ************************ Task & Filter Dialogs **************************/

  private todoTaskDialog(): void {
    this.dialog.open(InquiryDialogComponent, {
      width: '700px',
      height: '90vh',
      data: {
        title: 'New Inquiry',
        type: 'ShowTask',
        submit_button_text: 'Add Inquiry'
      },
      panelClass: 'todo-model-container'
    });
  }

  private filterSearchModal(): void {
    this.dialog.open(AttributeSearchDailogComponent, {
      data: { isfrom: 'apply-filter' },
      panelClass: 'standard-dialog'
    });
  }

  private filterSettingModal(): void {
    this.dialog.open(SearchFilterComponent, {
      panelClass: 'active-filter-container',
      data: { isfrom: 'active-filter' }
    });
  }

  private applyAttributePin(pins: any[]): void {
    this.analyzeService.setAttributeFilters(pins);
    this.initAnalyzeData();
  }

  private groupSmallNodes(): void {
    this.cookiesService.setCookie('sankey_group', this.groupLowImpactNodes.value);
    setTimeout(() => this.emitCustomEvent('back-to-default'), 10);
  }

  /**
   * Determines whether to show the "Show All Data" toggle switch.
   */
  private setToggleSwitch(): boolean {
    const isToggleAvailable = !!this.analyzeDataset?.toggle_on_field || !!this.analyzeDataset?.isToggleOnCostTable;
    const isAllowedView = ['sankey', 'grid', 'costing-grid'].includes(this.analyzeView);
    const isEnvSankey = this.app?.category === 'Environment' && this.analyzeView === 'sankey';

    this.showAllToggleSwitch = isToggleAvailable && isAllowedView && !isEnvSankey;
    return this.showAllToggleSwitch;
  }

  private toggleShowAllDataSwitch(): void {
    this.globalDataService.setAnalysisFilter(this.showAllData.value);
    this.emitCustomEvent('toggle-all-switch');
    this.costingGridWrapperComponent?.showAllData();
  }

  private openSidePanelFromSankeyLink(): void {
    this.openDrawer();
  }

  private openSidePanelFromView(): void {
    this.openDrawer();
    this.resizeSankeyEvent();
  }

  private populateSidePanel(row?: any): void {
    this.populateSidePanelForRid(row.id);
  }

  private populateSidePanelForRid(rid: string) {
    this.makersiteService.getTileCards(rid).subscribe((results) => {
      this.productInfo = results;
      this.cd.detectChanges();
    });
    this.getSidePanelDetails(this.sanitizeRid(rid));
  }

  private openSidePanel(row?: any): void {
    const shouldOpenDrawer = !['choropleth', 'heatmap'].includes(this.analyzeView);

    if (shouldOpenDrawer) this.openDrawer();
  }

  private closeSidepanel(isToggleButtonShow = true): void {
    this.isSidePanelShow = isToggleButtonShow;
    this.drawer?.close();
    this.resizeSankeyEvent();
  }

  /**
   * Resizes the Sankey or Graph view when necessary.
   */
  private resizeSankeyEvent(): void {
    if (['sankey', 'graph'].includes(this.analyzeView)) {
      this.svgResize = true;
      this.emitCustomEvent('svg-resize');
    }
  }

  /** ************************ Drawer Management **************************/

  private openDrawer(): void {
    this.isSidePanelShow = false;
    setTimeout(() => this.drawer?.open(), 0);
  }

  /**
   * Emits a custom event via the event delegator service.
   */
  private emitCustomEvent(action: string): void {
    this.eventDelegatorService.emiter('custom-ngx-event', {
      action,
      controller: window.location.pathname
    });
  }

  validateSelectedNode() {
    const product = this.productInfo?.[0];
    const rootRid = this.sanitizeRid(this.data.rid);
    const aiValidationDialogRef = this.dialog.open(AiValidationDialogComponent, {
      width: '50vw',
      data: { productDetails: product, rootRid }
    });
    aiValidationDialogRef.afterClosed().subscribe((value) => {
      if (value) {
        this.updateProductDetails(value, product);
      }
    });
  }

  updateProductDetails(value, product) {
    if (value) {
      product.validation_status = value.status;
      this.reusableService.productValidationChanged(product);
      this.updateSelectedNodeStatus(product);
    }
  }

  changeSelectedNodeStatusFromGrid(status: AIValidationStatuses) {
    const product = this.productInfo?.[0];
    const rid = product?.rid.replace('#', '');
    const rootRid = this.sanitizeRid(this.data.rid);
    this.updatingAIStatus = true;
    this.makersiteService
      .changeAINodeStatusAndCleanCache(status, rid, rootRid)
      .pipe(
        finalize(() => {
          this.updatingAIStatus = false;
        })
      )
      .subscribe({
        next: (value) => {
          if (value) {
            this.updateProductDetails({ status }, product);
          }
        },
        error: (value) => {
          this.reusableService.productValidationChanged(null);
        }
      });
  }

  updateSelectedNodeStatus(product: any) {
    if (product) {
      this.analyzeService.mutateNodeData(product);
    }
  }
}
